/**
 * HealthBars — world-space bars above remote players.
 *
 * Two sprites per player: a dark backing and a coloured fill whose X scale is
 * the health fraction. Sprites, not DOM, because eight bars projected and
 * written to the DOM every frame is eight layout invalidations per frame; and
 * not canvas textures, because a bar that changes 30 times a second would mean
 * 30 texture uploads.
 *
 * The value drawn is always the server's. `health` comes off the snapshot, and
 * the bar has no local prediction path at all — a health bar that guesses is a
 * health bar that lies to a player with money in the pot.
 */

import * as THREE from 'three/webgpu';
import { CHARACTERS } from '@chaos/protocol';

const BAR_WIDTH = 1.5;
const BAR_HEIGHT = 0.22;
const BAR_LIFT = 2.25;
/** Beyond this distance the bar is hidden — it would be a 2px smear anyway. */
const MAX_VISIBLE_DISTANCE = 55;

/**
 * The backing is an OUTLINE, not a shadow.
 *
 * It used to be a 72%-opaque near-black behind the fill, padded by 0.07 — over
 * a dark platform that is nearly invisible, and the bar lost its edges exactly
 * where the arena is darkest. Opaque ink, padded enough to read as a stroke at
 * distance, is the same idea as the 3px border on every HUD element and it is
 * what makes these legible against a bright deck AND a dark wall.
 */
const BAR_INK = 0x0d0520;
const BAR_PAD_X = 0.14;
const BAR_PAD_Y = 0.12;

/* Health colours from the mockups' palette, so the bar over a head and the bar
   on your own HUD are the same three colours rather than two families that
   nearly match. */
const HEALTHY = new THREE.Color(0x4ade80);
const HURT = new THREE.Color(0xffc107);
const CRITICAL = new THREE.Color(0xff3d3d);

const _scratch = new THREE.Color();

function makeSprite(colorHex, opacity) {
  const material = new THREE.SpriteNodeMaterial({ color: colorHex, transparent: true });
  material.opacity = opacity;
  material.depthTest = false;
  material.depthWrite = false;
  const sprite = new THREE.Sprite(material);
  sprite.userData.noOutline = true;
  return sprite;
}

/**
 * Attaches a bar to a character group. Idempotent.
 *
 * The bar is parented to the character so it inherits position for free, and
 * `renderOrder` puts it above the body — a bar that z-fights a shoulder is
 * unreadable at exactly the moment it matters.
 */
export function attachHealthBar(group, characterId) {
  const existing = group.userData.healthBar;
  if (existing) {
    // A player who swaps characters between rounds keeps their mesh group; a
    // bar still dividing by the OLD maxHealth would draw the wrong fraction.
    if (existing.characterId !== characterId) {
      existing.characterId = characterId;
      existing.maxHealth = (CHARACTERS[characterId] ?? CHARACTERS.volt).maxHealth;
    }
    return existing;
  }

  const def = CHARACTERS[characterId] ?? CHARACTERS.volt;
  const holder = new THREE.Group();
  holder.position.y = BAR_LIFT;
  holder.renderOrder = 20;

  const back = makeSprite(BAR_INK, 1);
  back.scale.set(BAR_WIDTH + BAR_PAD_X, BAR_HEIGHT + BAR_PAD_Y, 1);
  back.renderOrder = 20;
  holder.add(back);

  const fill = makeSprite(def.colorHex, 1);
  fill.scale.set(BAR_WIDTH, BAR_HEIGHT, 1);
  fill.renderOrder = 21;
  holder.add(fill);

  group.add(holder);
  const bar = { holder, back, fill, characterId, maxHealth: def.maxHealth };
  group.userData.healthBar = bar;
  return bar;
}

export function removeHealthBar(group) {
  const bar = group.userData.healthBar;
  if (!bar) return;
  group.remove(bar.holder);
  bar.back.material.dispose();
  bar.fill.material.dispose();
  group.userData.healthBar = null;
}

/**
 * Updates one bar.
 *
 * A sprite scales about its centre, so shrinking the fill alone would empty it
 * from both ends. Shifting X by half the lost width pins it to the left edge,
 * which is the only way a bar reads as draining rather than closing.
 */
export function updateHealthBar(group, health, cameraPosition) {
  const bar = group.userData.healthBar;
  if (!bar) return;

  const distance = cameraPosition.distanceTo(group.position);
  const visible = distance < MAX_VISIBLE_DISTANCE && health > 0;
  bar.holder.visible = visible;
  if (!visible) return;

  // UNDO THE BODY'S ROTATION FIRST.
  //
  // The bar is parented to the character group so it inherits position for
  // free — but that group is turned to face the player's aim, and the drain
  // offset below is a translation along the holder's local X. Inheriting the
  // yaw meant that offset swung around the body: facing one way the bar drained
  // from the left, facing another it slid right, and edge-on it vanished into
  // the screen and the bar looked centred. Reported as the bar being "out of
  // place, displaced" — which is exactly what it was doing, once per rotation.
  //
  // The camera is fixed at yaw 0, so cancelling the parent's yaw leaves local
  // +X pointing at screen-right, which is what "the left edge of the bar" has
  // to mean. `StatusAura` cancels it for the same reason.
  bar.holder.rotation.y = -group.rotation.y;

  const frac = Math.max(0, Math.min(1, health / bar.maxHealth));
  const width = BAR_WIDTH * frac;
  bar.fill.scale.x = Math.max(0.001, width);
  bar.fill.position.x = -(BAR_WIDTH - width) / 2;

  // Colour carries the same information as length, which is what makes the bar
  // readable in peripheral vision during a fight.
  if (frac > 0.5) {
    _scratch.copy(HURT).lerp(HEALTHY, (frac - 0.5) * 2);
  } else {
    _scratch.copy(CRITICAL).lerp(HURT, frac * 2);
  }
  bar.fill.material.color.copy(_scratch);

  // Bars shrink with distance more slowly than perspective would, so a distant
  // opponent's health is still legible without dominating a nearby one.
  const scale = 1 + Math.min(distance / MAX_VISIBLE_DISTANCE, 1) * 0.9;
  bar.holder.scale.setScalar(scale);
}
